var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Card, CardContent, Typography, Button, Alert, CircularProgress, Stack, Divider, ThemeProvider, Container, } from "@mui/material";
import { CameraAlt as CameraIcon, CheckCircle as CheckCircleIcon, Refresh as RefreshIcon, } from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import { restoreSession, useProfileAuthStore } from "./store/ProfileStore";
import { useAgeVerificationStore, AgeVerificationStatus, } from "./store/AgeVerificationStore";
import { useCacheBusterStore } from "./util/UrlUtils";
import { useLogger } from "./logger";
import Footer from "./Footer";
import getTheme from "./Theme";
const LOGPREFIX = "AgeVerification: ";
// Camera capture component
const CameraCapture = ({ onCapture, disabled, }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [capturing, setCapturing] = useState(false);
    const startCameraAndCapture = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        console.log("CameraCapture: Starting camera and capture process");
        setCapturing(true);
        try {
            console.log("CameraCapture: Checking if navigator.mediaDevices exists:", !!navigator.mediaDevices);
            console.log("CameraCapture: Checking if getUserMedia exists:", !!((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia));
            console.log("CameraCapture: Requesting camera access...");
            const mediaStream = yield navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: "user",
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                },
            });
            console.log("CameraCapture: Camera access granted, stream received");
            console.log("CameraCapture: videoRef.current exists:", !!videoRef.current);
            // Set camera active first to trigger video element rendering
            setCameraActive(true);
            setStream(mediaStream);
            // Wait for video element to be rendered, then set up the stream
            const setupVideo = () => {
                if (videoRef.current) {
                    console.log("CameraCapture: Setting video source...");
                    videoRef.current.srcObject = mediaStream;
                    console.log("CameraCapture: Camera started, waiting for metadata");
                    // Wait for video to be ready and add a small delay for camera to stabilize
                    videoRef.current.onloadedmetadata = () => {
                        console.log("CameraCapture: Video metadata loaded, starting capture timer");
                        setTimeout(() => {
                            console.log("CameraCapture: Timer expired, calling capturePhoto");
                            capturePhoto();
                        }, 1500); // 1.5 second delay to let camera stabilize
                    };
                    // Fallback in case onloadedmetadata doesn't fire
                    setTimeout(() => {
                        var _a;
                        console.log("CameraCapture: Fallback timer check - capturing:", capturing, "readyState:", (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.readyState);
                        if (capturing &&
                            videoRef.current &&
                            videoRef.current.readyState >= 2) {
                            console.log("CameraCapture: Fallback timer - forcing capture");
                            capturePhoto();
                        }
                        else {
                            console.log("CameraCapture: Fallback timer - conditions not met, not capturing");
                        }
                    }, 3000); // 3 second fallback
                }
                else {
                    console.log("CameraCapture: Video ref still not available, retrying in 100ms");
                    setTimeout(setupVideo, 100); // Retry after 100ms
                }
            };
            // Start the video setup process
            setTimeout(setupVideo, 50); // Small delay to let React render
        }
        catch (error) {
            console.error("CameraCapture: Failed to start camera:", error);
            console.error("CameraCapture: Error details:", error.name, error.message);
            alert("Unable to access camera. Please check your permissions.");
            setCapturing(false);
            setCameraActive(false);
        }
    });
    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
            setCameraActive(false);
            setCapturing(false);
        }
    };
    const capturePhoto = () => {
        console.log("CameraCapture: capturePhoto called");
        console.log("CameraCapture: videoRef.current exists:", !!videoRef.current);
        console.log("CameraCapture: canvasRef.current exists:", !!canvasRef.current);
        if (videoRef.current && canvasRef.current) {
            console.log("CameraCapture: Video readyState:", videoRef.current.readyState);
            console.log("CameraCapture: Video dimensions:", videoRef.current.videoWidth, "x", videoRef.current.videoHeight);
            const context = canvasRef.current.getContext("2d");
            if (context) {
                console.log("CameraCapture: Drawing image to canvas");
                context.drawImage(videoRef.current, 0, 0, 640, 480);
                console.log("CameraCapture: Converting canvas to blob");
                canvasRef.current.toBlob((blob) => {
                    console.log("CameraCapture: Blob created, size:", blob ? blob.size : "null");
                    if (blob) {
                        const file = new File([blob], `selfie_${Date.now()}.jpg`, {
                            type: "image/jpeg",
                            lastModified: Date.now(),
                        });
                        console.log("CameraCapture: File created, calling onCapture");
                        onCapture(file);
                        console.log("CameraCapture: Stopping camera");
                        stopCamera();
                    }
                    else {
                        console.error("CameraCapture: Failed to create blob from canvas");
                        setCapturing(false);
                    }
                }, "image/jpeg", 0.95);
            }
            else {
                console.error("CameraCapture: Failed to get canvas context");
                setCapturing(false);
            }
        }
        else {
            console.error("CameraCapture: Missing video or canvas ref");
            setCapturing(false);
        }
    };
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [stream]);
    return (_jsxs(Box, Object.assign({ sx: { textAlign: "center" } }, { children: [cameraActive ? (_jsxs(Box, { children: [_jsx("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: true, style: {
                            width: "100%",
                            maxWidth: 400,
                            borderRadius: 8,
                            marginBottom: 16,
                        } }), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: capturing ? "Capturing photo..." : "Photo captured!" }))] })) : (_jsx(Button, Object.assign({ variant: "contained", onClick: startCameraAndCapture, startIcon: _jsx(CameraIcon, {}), disabled: disabled || capturing, size: "large" }, { children: capturing ? "Taking Photo..." : "Take Photo" }))), _jsx("canvas", { ref: canvasRef, style: { display: "none" }, width: 640, height: 480 })] })));
};
export default function AgeVerification() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = getTheme();
    const auth = useProfileAuthStore();
    const ageStore = useAgeVerificationStore();
    const cacheBuster = useCacheBusterStore();
    const logger = useLogger();
    const [selfieImage, setSelfieImage] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    // Get and validate return URL
    const returnUrl = searchParams.get("returnUrl");
    const hasValidReturnUrl = returnUrl &&
        (returnUrl.startsWith("/") || returnUrl.startsWith(window.location.origin));
    // Handle navigation after successful verification
    const handleSuccessNavigation = () => {
        if (hasValidReturnUrl) {
            logger.log(LOGPREFIX + "Redirecting to return URL: " + returnUrl);
            if (returnUrl.startsWith("/")) {
                navigate(returnUrl);
            }
            else {
                window.location.href = returnUrl;
            }
        }
        else {
            logger.log(LOGPREFIX + "No valid return URL, going to Home");
            navigate("/Home");
        }
    };
    // Check authentication
    useEffect(() => {
        if (!auth.isSignedIn() || !auth.userId) {
            logger.log(LOGPREFIX + "User not signed in, attempting to restore session");
            if (!restoreSession(auth, logger)) {
                logger.log(LOGPREFIX + "Session restore failed, redirecting to Welcome");
                navigate("/Welcome");
            }
        }
        else {
            // Load current verification status
            logger.log(LOGPREFIX + "Loading verification status for user: " + auth.userId);
            ageStore.loadStatus(auth.userId, auth, cacheBuster, logger);
        }
    }, [auth.isSignedIn(), auth.userId]);
    // Handle file upload
    const handleFileChange = (event) => {
        var _a;
        const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (file) {
            setSelfieImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                var _a;
                setImagePreview((_a = e.target) === null || _a === void 0 ? void 0 : _a.result);
            };
            reader.readAsDataURL(file);
        }
    };
    // Handle camera capture
    const handleCameraCapture = (file) => {
        console.log("AgeVerification: handleCameraCapture called with file:", file);
        setSelfieImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            var _a;
            const result = (_a = e.target) === null || _a === void 0 ? void 0 : _a.result;
            console.log("AgeVerification: Image preview data URL created, length:", result ? result.length : 0);
            setImagePreview(result);
            console.log("AgeVerification: imagePreview state updated, should show preview now");
        };
        reader.onerror = (error) => {
            console.error("AgeVerification: FileReader error:", error);
        };
        reader.readAsDataURL(file);
    };
    // Handle form submission
    const handleSubmit = () => __awaiter(this, void 0, void 0, function* () {
        if (!selfieImage) {
            alert("Please provide a selfie.");
            return;
        }
        const success = yield ageStore.submitVerification(18, // Declare user is 18+ (required for adult verification)
        selfieImage, auth, cacheBuster, logger);
        if (success) {
            // Clear form
            setSelfieImage(null);
            setImagePreview("");
        }
    });
    // Render based on verification status
    const renderContent = () => {
        var _a, _b, _c, _d;
        if (ageStore.loading) {
            return (_jsxs(Box, Object.assign({ sx: { textAlign: "center", py: 4 } }, { children: [_jsx(CircularProgress, {}), _jsx(Typography, Object.assign({ sx: { mt: 2 } }, { children: "Processing..." }))] })));
        }
        if (((_a = ageStore.metadata) === null || _a === void 0 ? void 0 : _a.status) === AgeVerificationStatus.VERIFIED) {
            return (_jsxs(Box, Object.assign({ sx: { textAlign: "center", py: 4 } }, { children: [_jsx(CheckCircleIcon, { sx: { fontSize: 80, color: "success.main", mb: 2 } }), _jsx(Typography, Object.assign({ variant: "h4", gutterBottom: true }, { children: "Age Verification Complete" })), _jsxs(Typography, Object.assign({ variant: "h6", color: "text.secondary", gutterBottom: true }, { children: ["Status:", " ", ageStore.metadata.isAdult ? "✅ Adult (18+)" : "⚠️ Under 18"] })), ageStore.metadata.verifiedAt && (_jsxs(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: ["Verified on:", " ", new Date(ageStore.metadata.verifiedAt).toLocaleDateString()] }))), ageStore.metadata.ageEstimate && (_jsxs(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { mt: 1 } }, { children: ["Estimated age range: ", ageStore.metadata.ageEstimate.low, " -", " ", ageStore.metadata.ageEstimate.high, " years"] }))), _jsx(Button, Object.assign({ variant: "contained", onClick: handleSuccessNavigation, sx: { mt: 3 } }, { children: hasValidReturnUrl ? "Continue..." : "Return to Home" }))] })));
        }
        const isFailed = ((_b = ageStore.metadata) === null || _b === void 0 ? void 0 : _b.status) === AgeVerificationStatus.FAILED;
        return (_jsxs(Box, { children: [isFailed && (_jsxs(Alert, Object.assign({ severity: "warning", sx: { mb: 3 } }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle1", gutterBottom: true }, { children: "Verification Failed" })), !((_c = ageStore.metadata) === null || _c === void 0 ? void 0 : _c.isAdult) && (_jsx(Typography, Object.assign({ variant: "body2" }, { children: "You must be 18 or older to access certain content." }))), _jsxs(Typography, Object.assign({ variant: "body2" }, { children: ["Attempts: ", ((_d = ageStore.metadata) === null || _d === void 0 ? void 0 : _d.attemptCount) || 0] }))] }))), ageStore.error &&
                    !ageStore.error.toLowerCase().includes("not verified") &&
                    !ageStore.error.toLowerCase().includes("no verification") &&
                    !ageStore.error
                        .toLowerCase()
                        .includes("failed to load verification status") &&
                    !ageStore.error.toLowerCase().includes("verification status") && (_jsx(Alert, Object.assign({ severity: "error", sx: { mb: 3 } }, { children: ageStore.error }))), (!ageStore.metadata ||
                    !ageStore.metadata.status ||
                    ageStore.metadata.status === AgeVerificationStatus.NOT_VERIFIED) && (_jsx(Alert, Object.assign({ severity: "info", sx: { mb: 3, backgroundColor: "#e3f2fd" } }, { children: _jsx(Typography, Object.assign({ variant: "body2", sx: { fontWeight: 500 } }, { children: "Age verification status: Not yet verified" })) }))), _jsx(Typography, Object.assign({ variant: "h5", gutterBottom: true }, { children: "Age Verification Required" })), _jsxs(Alert, Object.assign({ severity: "info", sx: { mb: 3 } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", paragraph: true }, { children: _jsx("strong", { children: "US state laws require age verification to access adult content." }) })), _jsx(Typography, Object.assign({ variant: "body2", paragraph: true }, { children: "You can continue viewing non-adult content without verification, but age verification is required to access age-restricted content on this platform." })), _jsx(Typography, Object.assign({ variant: "body2", paragraph: true }, { children: _jsx("strong", { children: "Your privacy is protected:" }) })), _jsx(Typography, Object.assign({ variant: "body2", paragraph: true }, { children: "\u2022 Take a selfie using your camera or upload a photo" })), _jsx(Typography, Object.assign({ variant: "body2", paragraph: true }, { children: "\u2022 Your photo is used only for age estimation by an Artificial Intelligence (AI) model" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: "\u2022 The image is not stored after verification and is not reviewed by humans" }))] })), _jsxs(Stack, Object.assign({ spacing: 3 }, { children: [imagePreview && (_jsxs(Box, Object.assign({ sx: { textAlign: "center", mb: 2 } }, { children: [_jsx("img", { src: imagePreview, alt: "Selfie preview", style: {
                                        maxWidth: "100%",
                                        maxHeight: 300,
                                        borderRadius: 8,
                                    } }), _jsxs(Box, Object.assign({ sx: {
                                        mt: 2,
                                        display: "flex",
                                        gap: 2,
                                        justifyContent: "center",
                                    } }, { children: [_jsx(Button, Object.assign({ variant: "contained", size: "large", onClick: handleSubmit, disabled: ageStore.loading, startIcon: _jsx(CheckCircleIcon, {}) }, { children: ageStore.loading
                                                ? "Verifying..."
                                                : "Submit for Verification" })), _jsx(Button, Object.assign({ variant: "outlined", size: "small", onClick: () => {
                                                setSelfieImage(null);
                                                setImagePreview("");
                                            }, startIcon: _jsx(RefreshIcon, {}) }, { children: "Retake Photo" }))] }))] }))), !imagePreview && (_jsxs(_Fragment, { children: [_jsx(CameraCapture, { onCapture: handleCameraCapture, disabled: ageStore.loading }), _jsx(Divider, { children: "OR" }), _jsxs(Box, { children: [_jsx("input", { accept: "image/*", style: { display: "none" }, id: "selfie-file-upload", type: "file", onChange: handleFileChange, disabled: ageStore.loading }), _jsx("label", Object.assign({ htmlFor: "selfie-file-upload" }, { children: _jsx(Button, Object.assign({ variant: "outlined", component: "span", fullWidth: true, disabled: ageStore.loading }, { children: "Upload from Device" })) }))] })] })), isFailed && (_jsx(Button, Object.assign({ variant: "text", onClick: () => {
                                setSelfieImage(null);
                                setImagePreview("");
                                ageStore.reset();
                            }, startIcon: _jsx(RefreshIcon, {}) }, { children: "Try Again" })))] }))] }));
    };
    return (_jsxs(ThemeProvider, Object.assign({ theme: theme }, { children: [_jsxs(Helmet, { children: [_jsx("title", { children: "Age Verification - MediaShare.Link" }), _jsx("meta", { name: "description", content: "Verify your age to access age-restricted content on MediaShare.Link" })] }), _jsxs(Box, Object.assign({ sx: {
                    minHeight: "100vh",
                    backgroundColor: "#f8f6ff",
                    display: "flex",
                    flexDirection: "column",
                } }, { children: [_jsx(Container, Object.assign({ maxWidth: "sm", sx: { flex: 1, py: 4 } }, { children: _jsx(Card, Object.assign({ elevation: 3 }, { children: _jsxs(CardContent, Object.assign({ sx: { p: 4 } }, { children: [_jsx(Box, Object.assign({ sx: { textAlign: "center", mb: 3 } }, { children: _jsx("img", { src: "img/logo.svg", alt: "MediaShare.Link Logo", style: {
                                                width: "200px",
                                                maxWidth: "100%",
                                                height: "auto",
                                                cursor: "pointer",
                                            }, onClick: () => navigate("/") }) })), renderContent()] })) })) })), _jsx(Footer, {})] }))] })));
}
